'use client';

import type { Variants, UseInViewOptions } from 'framer-motion';

import { twMerge } from 'tailwind-merge';
import { useRef, useMemo, useEffect } from 'react';
import { m, useInView, useAnimation } from 'framer-motion';

import { Typography } from '../ui';
import { varFade, varContainer } from './variants';
import { animateTextClasses } from '../../core/constants';

// ----------------------------------------------------------------------

export type AnimateTextProps = React.ComponentProps<typeof Typography> & {
  variants?: Variants;
  repeatDelayMs?: number;
  textContent: string | string[];
  once?: UseInViewOptions['once'];
  amount?: UseInViewOptions['amount'];
};

export function AnimateText({
  variants,
  className,
  textContent,
  once = true,
  amount = 1 / 3,
  variant = 'body1',
  component,
  repeatDelayMs = 100, // 1000 = 1s
  ...other
}: AnimateTextProps) {
  const textRef = useRef(null);

  const animationControls = useAnimation();

  const textArray = useMemo(
    () => (Array.isArray(textContent) ? textContent : [textContent]),
    [textContent]
  );

  const isInView = useInView(textRef, { once, amount });

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const triggerAnimation = () => {
      if (repeatDelayMs) {
        timeout = setTimeout(async () => {
          await animationControls.start('initial');
          animationControls.start('animate');
        }, repeatDelayMs);
      } else {
        animationControls.start('animate');
      }
    };

    if (isInView) {
      triggerAnimation();
    } else {
      animationControls.start('initial');
    }

    return () => clearTimeout(timeout);
  }, [animationControls, isInView, repeatDelayMs]);

  return (
    <Typography
      variant={variant}
      component={component}
      className={twMerge(animateTextClasses.root, className)}
      {...other}
    >
      <span
        className={
          animateTextClasses.srOnly +
          ' w-px h-px m-[-1px] border-0 overflow-hidden absolute whitespace-nowrap clip-rect-0'
        }
      >
        {textArray.join(' ')}
      </span>

      <m.span
        aria-hidden
        ref={textRef}
        initial="initial"
        animate={animationControls}
        exit="exit"
        variants={varContainer()}
        className={animateTextClasses.lines}
      >
        {textArray?.map((line, lineIndex) => (
          <span
            key={`${line}-${lineIndex}`}
            data-index={lineIndex}
            className={twMerge(animateTextClasses.line, 'block')}
          >
            {line.split(' ').map((word, wordIndex) => {
              const lastWordInline = line.split(' ')[line.split(' ').length - 1];

              return (
                <span
                  key={`${word}-${wordIndex}`}
                  data-index={wordIndex}
                  className={twMerge(animateTextClasses.word, 'inline-block')}
                >
                  {word.split('').map((char, charIndex) => (
                    <m.span
                      key={`${char}-${charIndex}`}
                      variants={variants ?? varFade('in')}
                      data-index={charIndex}
                      className={twMerge(animateTextClasses.char, 'inline-block')}
                    >
                      {char}
                    </m.span>
                  ))}

                  {lastWordInline !== word && (
                    <span className={twMerge(animateTextClasses.space, 'inline-block')}>
                      &nbsp;
                    </span>
                  )}
                </span>
              );
            })}
          </span>
        ))}
      </m.span>
    </Typography>
  );
}

// ----------------------------------------------------------------------
